// features/usage/components/UsageDashboard.tsx
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useUsageSummary, useDeviceUsage, useTimeseries } from '../hooks/useUsage';
import { ServiceUnavailable } from '@/shared/components/ServiceUnavailable';

function fmtCredits(n: number) {
  return n.toLocaleString('en-US');
}
function fmtUsd(n: number) {
  return `$ ${n.toFixed(2)}`;
}
function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
function pctBar(pct: number) {
  const clamped = Math.min(pct, 100);
  const color = pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="mt-1 h-2 w-full rounded-full bg-neutral-800">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

export default function UsageDashboard() {
  useDocumentTitle('Usage');

  const summary    = useUsageSummary();
  const devices    = useDeviceUsage();
  const timeseries = useTimeseries();

  const s = summary.data;

  // When ALL queries are errored, show a full-page unavailable fallback
  const allErrored = summary.isError && devices.isError && timeseries.isError;
  const isServiceDown = allErrored || (summary.error as any)?.errorCode === 'service_unavailable';

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold text-neutral-100">Usage & Metering</h1>

      {isServiceDown && (
        <ServiceUnavailable
          title="Usage data unavailable"
          detail="The orchestrator service is recovering. Usage metrics will appear automatically once the service is back online."
          onRetry={() => { summary.refetch(); devices.refetch(); timeseries.refetch(); }}
          isRetrying={summary.isRefetching || devices.isRefetching || timeseries.isRefetching}
        />
      )}

      {/* ── Summary cards ─────────────────────────────────────────── */}
      {summary.isLoading && <p className="text-sm text-neutral-500">Loading summary…</p>}
      {summary.isError && !isServiceDown && <p className="text-sm text-red-400">Failed to load summary.</p>}
      {s && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-md pac-glass p-4 border border-neutral-800">
            <p className="text-xs text-neutral-400">Credit Balance</p>
            <p className="text-2xl font-semibold mt-1 text-emerald-400">{fmtCredits(s.creditBalance)}</p>
            <p className="text-xs text-neutral-500 mt-1">
              Lifetime: {fmtCredits(s.lifetimeCredits)} granted · {fmtCredits(s.lifetimeConsumed)} used
            </p>
          </div>

          <div className="rounded-md pac-glass p-4 border border-neutral-800">
            <p className="text-xs text-neutral-400">Today's Cost</p>
            <p className="text-2xl font-semibold mt-1">{fmtUsd(s.todayCostUsd)}</p>
            <p className="text-xs text-neutral-500 mt-1">{fmtCredits(s.creditsPerUsd)} credits / $1</p>
          </div>

          <div className="rounded-md pac-glass p-4 border border-neutral-800">
            <p className="text-xs text-neutral-400">Today's Requests</p>
            <p className="text-2xl font-semibold mt-1">{fmtCredits(s.todayRequests)}</p>
            <p className="text-xs text-neutral-500 mt-1">
              {fmtCredits(s.todayRows)} rows · {fmtBytes(s.todayBytes)}
            </p>
          </div>

          <div className="rounded-md pac-glass p-4 border border-neutral-800">
            <p className="text-xs text-neutral-400">Hard Limit Usage</p>
            <p className="text-2xl font-semibold mt-1">{s.todayPctHardLimit.toFixed(1)}%</p>
            {pctBar(s.todayPctHardLimit)}
            <p className="text-xs text-neutral-500 mt-1">
              {fmtCredits(s.hardLimitDaily)} daily hard limit
            </p>
          </div>
        </div>
      )}

      {/* ── Timeseries (simple table for now) ─────────────────────── */}
      <div className="rounded-md pac-glass p-4 border border-neutral-800">
        <h2 className="text-sm font-medium text-neutral-300 mb-3">Daily Usage</h2>
        {timeseries.isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
        {timeseries.isError && !isServiceDown && <p className="text-sm text-red-400">Failed to load timeseries.</p>}
        {timeseries.data && timeseries.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-xs text-neutral-500 border-b border-neutral-800">
                  <th className="py-2 pr-4">Day</th>
                  <th className="py-2 pr-4 text-right">Requests</th>
                  <th className="py-2 pr-4 text-right">Rows</th>
                  <th className="py-2 pr-4 text-right">Bytes</th>
                  <th className="py-2 pr-4 text-right">Credits</th>
                  <th className="py-2 text-right">Devices</th>
                </tr>
              </thead>
              <tbody>
                {timeseries.data.items.map(t => (
                  <tr key={t.periodDay} className="border-b border-neutral-800/50 text-neutral-300">
                    <td className="py-2 pr-4 font-mono text-xs">{t.periodDay}</td>
                    <td className="py-2 pr-4 text-right">{fmtCredits(t.requestCount)}</td>
                    <td className="py-2 pr-4 text-right">{fmtCredits(t.rowCount)}</td>
                    <td className="py-2 pr-4 text-right">{fmtBytes(t.bytesIn)}</td>
                    <td className="py-2 pr-4 text-right">{fmtCredits(t.weightedCredits)}</td>
                    <td className="py-2 text-right">{t.deviceCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {timeseries.data && timeseries.data.items.length === 0 && (
          <p className="text-sm text-neutral-500">No timeseries data yet.</p>
        )}
      </div>

      {/* ── Device usage table ────────────────────────────────────── */}
      <div className="rounded-md pac-glass p-4 border border-neutral-800">
        <h2 className="text-sm font-medium text-neutral-300 mb-3">Per-Device Usage</h2>
        {devices.isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
        {devices.isError && !isServiceDown && <p className="text-sm text-red-400">Failed to load device usage.</p>}
        {devices.data && devices.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-xs text-neutral-500 border-b border-neutral-800">
                  <th className="py-2 pr-4">Device</th>
                  <th className="py-2 pr-4">Operation</th>
                  <th className="py-2 pr-4">Day</th>
                  <th className="py-2 pr-4 text-right">Requests</th>
                  <th className="py-2 pr-4 text-right">Rows</th>
                  <th className="py-2 text-right">Credits</th>
                </tr>
              </thead>
              <tbody>
                {devices.data.items.map((d, i) => (
                  <tr key={`${d.scopeKey}-${d.periodDay}-${d.opType}-${i}`} className="border-b border-neutral-800/50 text-neutral-300">
                    <td className="py-2 pr-4 font-mono text-xs">{d.scopeKey}</td>
                    <td className="py-2 pr-4">{d.opType}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{d.periodDay}</td>
                    <td className="py-2 pr-4 text-right">{fmtCredits(d.requestCount)}</td>
                    <td className="py-2 pr-4 text-right">{fmtCredits(d.rowCount)}</td>
                    <td className="py-2 text-right">{fmtCredits(d.weightedCredits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {devices.data && devices.data.items.length === 0 && (
          <p className="text-sm text-neutral-500">No device usage data yet.</p>
        )}
      </div>
    </section>
  );
}